/*
 * Hair quiz - Align theme
 * Steps, conditional logic, recommendation engine, customer save (native customer form) and cart.
 */
(() => {
  if (customElements.get('hair-quiz')) return;

  const STORAGE_KEY = 'hairQuiz:v1';
  const FLOW = ['intro', 'name', 'texture', 'coloured', 'colour', 'scalp', 'density', 'concerns', 'save', 'results'];
  const QUESTION_STEPS = ['name', 'texture', 'coloured', 'colour', 'scalp', 'density', 'concerns'];
  const AUTO_ADVANCE_MS = 320;
  const SETS = {
    set_revival: ['shampoo_nourishing', 'conditioner_nourishing', 'treatment_oil'],
    set_ritual: ['conditioner_volume', 'treatment_oil', 'tool_comb'],
    set_growth: ['shampoo_nourishing', 'treatment_scalp'],
    set_volume: ['shampoo_volume', 'conditioner_volume']
  };

  /* ---------- Recommendation engine ----------
   * Scores are based on the product descriptions in the catalogue.
   * Every slot keeps a ranked list of candidates and takes the first one that is
   * published and in stock, so the routine always contains a cleanser and at least
   * two products, even when a product is unavailable.
   */
  const BARS = ['bar_balancing', 'bar_calming', 'bar_moisture', 'bar_detox'];
  const SCALP_BAR = { oily: 'bar_balancing', sensitive: 'bar_calming', dry: 'bar_moisture', normal: 'bar_detox' };

  function recommend(a, isAvail) {
    const ok = (k) => (typeof isAvail === 'function' ? isAvail(k) : true);
    const has = (c) => a.concerns.includes(c);
    const curlyish = a.texture === 'curly' || a.texture === 'coily';
    const coloured = a.coloured === 'yes';
    const ranked = (list) => list.slice().sort((x, y) => y.score - x.score).map((x) => x.key);
    const firstOk = (keys, used) => keys.find((k) => ok(k) && !used.includes(k)) || null;

    const shampoos = ranked([
      { key: 'shampoo_colour', score: (coloured ? 4 : 0) + (coloured && has('dry_damaged') ? 1 : 0) },
      {
        key: 'shampoo_nourishing',
        score: 1 + (a.scalp === 'oily' ? 2 : 0) + (a.scalp === 'normal' || a.scalp === 'dry' ? 1 : 0) +
          (has('dry_damaged') ? 1 : 0) + (has('thinning') ? 3 : 0) + (has('lackluster') ? 1 : 0) + (curlyish ? 1 : 0)
      },
      {
        key: 'shampoo_volume',
        score: (has('volume') ? 4 : 0) + (a.density === 'low' ? 2 : 0) + (a.texture === 'straight' ? 1 : 0) +
          (has('lackluster') ? 1 : 0) - (a.density === 'high' ? 2 : 0) - (curlyish ? 2 : 0)
      }
    ]);
    const scalpBar = SCALP_BAR[a.scalp];
    const cleanseOrder = shampoos.concat(scalpBar ? [scalpBar] : [], BARS.filter((b) => b !== scalpBar));

    const conditionOrder = ranked([
      {
        key: 'conditioner_nourishing',
        score: (has('frizz') ? 3 : 0) + (has('tangled') ? 3 : 0) + (has('dry_damaged') ? 2 : 0) + (curlyish ? 2 : 0) +
          (a.density === 'high' ? 1 : 0) + (coloured ? 1 : 0)
      },
      {
        key: 'conditioner_volume',
        score: (has('volume') ? 3 : 0) + (a.density === 'low' ? 2 : 0) + (a.texture === 'straight' || a.texture === 'wavy' ? 1 : 0) +
          (has('thinning') ? 1 : 0) + (has('lackluster') ? 1 : 0)
      }
    ]).concat(['bar_conditioner']);

    const treatments = [
      {
        key: 'treatment_scalp',
        score: (has('thinning') ? 4 : 0) + (a.scalp === 'oily' ? 2 : 0) + (a.scalp === 'sensitive' ? 1 : 0) + (has('volume') ? 1 : 0)
      },
      {
        key: 'treatment_oil',
        score: (has('dry_damaged') ? 3 : 0) + (has('frizz') ? 2 : 0) + (curlyish ? 2 : 0) + (a.scalp === 'dry' ? 1 : 0) + (has('lackluster') ? 1 : 0)
      }
    ].sort((x, y) => y.score - x.score);

    const routine = [];
    const used = [];
    const add = (step, key) => {
      if (!key) return;
      routine.push({ step, key, reason: reasonFor(key, a) });
      used.push(key);
    };

    // 1. Cleanse - always present
    add('cleanse', firstOk(cleanseOrder, used));
    // 2. Condition
    add('condition', firstOk(conditionOrder, used));
    // 3. Treat - strong matches first, otherwise the best moderate match
    const strong = treatments.filter((t) => t.score >= 3 && ok(t.key));
    if (strong.length) strong.forEach((t) => add('treat', t.key));
    else {
      const moderate = treatments.find((t) => t.score >= 2 && ok(t.key));
      if (moderate) add('treat', moderate.key);
    }
    // 4. Tone
    if (coloured && (a.colour === 'blonde' || a.colour === 'silver') && ok('toner_bar')) add('tone', 'toner_bar');
    // 5. Detangle
    if ((has('tangled') || curlyish || a.density === 'high') && ok('tool_comb')) add('detangle', 'tool_comb');

    // Minimum of two products: fill with the best remaining treatment, then the comb
    const fillers = treatments.map((t) => ({ step: 'treat', key: t.key })).concat([{ step: 'detangle', key: 'tool_comb' }]);
    for (const f of fillers) {
      if (routine.length >= 2) break;
      if (ok(f.key) && !used.includes(f.key)) add(f.step, f.key);
    }

    // Keep the wash-day order: cleanse, condition, treat, tone, detangle
    const ORDER = ['cleanse', 'condition', 'treat', 'tone', 'detangle'];
    routine.sort((x, y) => ORDER.indexOf(x.step) - ORDER.indexOf(y.step));

    const inRoutine = (k) => used.includes(k);

    // Value sets: shown only when at least one of their products is in the routine
    // and no product in the set would replace one already chosen for that step.
    const slotOf = (k) => (k.startsWith('shampoo') || (k.startsWith('bar_') && k !== 'bar_conditioner') ? 'cleanse'
      : k.startsWith('conditioner') || k === 'bar_conditioner' ? 'condition'
      : k.startsWith('treatment') ? 'treat' : k.startsWith('tool') ? 'detangle' : k);
    const takenSlots = new Set(routine.filter((r) => r.step !== 'treat').map((r) => r.step));
    let set = null;
    Object.keys(SETS).forEach((setKey) => {
      if (!ok(setKey)) return;
      const items = SETS[setKey];
      const covered = items.filter((k) => inRoutine(k));
      const extras = items.filter((k) => !inRoutine(k));
      if (!covered.length) return;
      if (extras.some((k) => takenSlots.has(slotOf(k)))) return;
      const candidate = { key: setKey, covered, extras };
      if (!set || covered.length > set.covered.length || (covered.length === set.covered.length && extras.length < set.extras.length)) set = candidate;
    });

    // Zero-waste swaps: solid alternatives that are not already in the routine
    const swaps = [];
    if (scalpBar && ok(scalpBar) && !inRoutine(scalpBar) && !routine.some((r) => r.step === 'cleanse' && r.key.startsWith('bar_'))) {
      swaps.push({ key: scalpBar, reason: reasonFor(scalpBar, a) });
    }
    if ((inRoutine('conditioner_nourishing') || has('tangled') || has('dry_damaged')) && ok('bar_conditioner') && !inRoutine('bar_conditioner')) {
      swaps.push({ key: 'bar_conditioner', reason: reasonFor('bar_conditioner', a) });
    }

    return { routine, set, swaps };
  }

  let REASONS = {};
  function reasonFor(key, a) {
    const has = (c) => a.concerns.includes(c);
    const curlyish = a.texture === 'curly' || a.texture === 'coily';
    let variant = 'default';
    if (key === 'shampoo_nourishing') variant = has('thinning') ? 'thinning' : a.scalp === 'oily' ? 'oily' : 'default';
    if (key === 'conditioner_nourishing') variant = has('frizz') ? 'frizz' : has('tangled') ? 'tangled' : 'default';
    if (key === 'treatment_oil') variant = curlyish ? 'curly' : 'default';
    return REASONS[`${key}_${variant}`] || REASONS[`${key}_default`] || '';
  }

  /* ---------- Helpers ---------- */
  const esc = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const store = {
    read() {
      try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || null; } catch (e) { return null; }
    },
    write(data) {
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) { /* storage unavailable */ }
    }
  };

  function track(event, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, detail || {}));
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, detail || {});
      }
    } catch (e) { /* analytics optional */ }
  }

  const emptyAnswers = () => ({ name: '', texture: null, coloured: null, colour: null, scalp: null, density: null, concerns: [] });

  /* ---------- Custom element ---------- */
  class HairQuiz extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-hq-config]');
      if (!configEl) return;
      try { this.cfg = JSON.parse(configEl.textContent); } catch (e) { console.error('Hair quiz: invalid config', e); return; }
      REASONS = (this.cfg.text && this.cfg.text.reasons) || {};
      this.isAvail = (k) => { const prod = (this.cfg.products || {})[k]; return !!(prod && prod.available); };

      this.answers = emptyAnswers();
      if (this.cfg.customerFirstName) this.answers.name = this.cfg.customerFirstName;
      this.current = 'intro';
      this.viaPointer = false;
      this.advanceTimer = null;

      this.stage = this.querySelector('.hq__stage');
      this.bar = this.querySelector('[data-hq-bar]');
      this.ridge = this.querySelector('[data-hq-ridge]');
      this.progress = this.querySelector('[data-hq-progress]');
      this.count = this.querySelector('[data-hq-count]');
      this.resultsRoot = this.querySelector('[data-hq-results]');

      this.bindEvents();
      this.restore();
    }

    /* ----- State ----- */
    restore() {
      const saved = store.read();
      if (!saved || !saved.answers) return;
      this.saved = saved;
      const resume = this.querySelector('[data-hq-resume]');
      if (resume) resume.hidden = false;

      // Back from a native form submission (fallback path): show the results straight away.
      const params = new URLSearchParams(window.location.search);
      if (params.get('hq') === 'results' || params.get('customer_posted') === 'true') {
        this.answers = Object.assign(emptyAnswers(), saved.answers);
        if (params.get('customer_posted') === 'true') this.persist({ savedToAccount: true });
        this.showSavedResults();
        if (window.history && window.history.replaceState) window.history.replaceState({}, '', window.location.pathname);
      }
    }

    persist(extra) {
      const prev = store.read() || {};
      store.write(Object.assign({}, prev, { answers: this.answers, updatedAt: new Date().toISOString() }, extra || {}));
    }

    /* ----- Events ----- */
    bindEvents() {
      this.addEventListener('pointerdown', (e) => {
        if (e.target.closest('.hq-option')) this.viaPointer = true;
      });

      this.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-hq-start]')) { track('hair_quiz_start'); this.go('name'); return; }
        if (t.closest('[data-hq-resume]')) { this.showSavedResults(); return; }
        if (t.closest('[data-hq-next]')) { this.next(); return; }
        if (t.closest('[data-hq-back]')) { this.back(); return; }
        if (t.closest('[data-hq-skip]')) { track('hair_quiz_save_skipped'); this.go('results'); return; }
        if (t.closest('[data-hq-retake]')) { e.preventDefault(); this.retake(); return; }
        const add = t.closest('[data-hq-add]');
        if (add) { this.addToCart([add.dataset.hqAdd], add); return; }
        const addAll = t.closest('[data-hq-add-all]');
        if (addAll) {
          const ids = Array.from(this.querySelectorAll('[data-hq-routine] [data-hq-add]')).map((b) => b.dataset.hqAdd).filter(Boolean);
          this.addToCart(ids, addAll);
        }
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-hq-input]');
        if (!input) return;
        const field = input.dataset.hqInput;
        if (field === 'concerns') {
          this.answers.concerns = Array.from(this.querySelectorAll('[data-hq-input="concerns"]:checked')).map((i) => i.value);
        } else if (input.type === 'radio') {
          this.answers[field] = input.value;
          if (field === 'coloured' && input.value === 'no') this.answers.colour = null;
          if (this.viaPointer) {
            clearTimeout(this.advanceTimer);
            this.advanceTimer = setTimeout(() => this.next(), AUTO_ADVANCE_MS);
          }
        }
        this.viaPointer = false;
        this.updateNextState();
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-hq-input="name"]')) {
          this.answers.name = e.target.value.trim();
          this.updateNextState();
        }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-hq-input="name"]')) {
          e.preventDefault();
          if (this.answers.name) this.next();
        }
      });

      const form = this.querySelector('form.hq-save');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.submitSave(form); });
    }

    /* ----- Navigation ----- */
    flow() {
      return FLOW.filter((step) => {
        if (step === 'colour') return this.answers.coloured === 'yes';
        if (step === 'save') return this.cfg.saveEnabled;
        return true;
      });
    }

    next() {
      clearTimeout(this.advanceTimer);
      if (!this.isComplete(this.current)) return;
      const flow = this.flow();
      const i = flow.indexOf(this.current);
      const nextStep = flow[i + 1];
      track('hair_quiz_answer', { step: this.current, value: this.valueFor(this.current) });
      if (this.current === 'concerns') this.onQuizComplete();
      if (nextStep) this.go(nextStep);
    }

    back() {
      clearTimeout(this.advanceTimer);
      const flow = this.flow();
      const i = flow.indexOf(this.current);
      if (i > 0) this.go(flow[i - 1], true);
    }

    go(step, isBack) {
      const target = this.querySelector(`[data-hq-step="${step}"]`);
      if (!target) return;
      this.querySelectorAll('[data-hq-step]').forEach((el) => {
        el.hidden = el !== target;
        el.classList.toggle('is-active', el === target);
        el.classList.toggle('is-back', el === target && !!isBack);
      });
      this.current = step;
      this.dataset.step = step;

      if (step === 'results') this.renderResults();
      this.fillName();
      this.updateProgress();
      this.updateNextState();

      const focusEl = step === 'name' ? target.querySelector('[data-hq-input="name"]') : target.querySelector('.hq-title');
      if (focusEl) focusEl.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top + window.scrollY - 24;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    isComplete(step) {
      const a = this.answers;
      switch (step) {
        case 'intro': return true;
        case 'name': return !!a.name;
        case 'concerns': return a.concerns.length > 0;
        case 'save': case 'results': return true;
        default: return !!a[step];
      }
    }

    valueFor(step) {
      if (step === 'concerns') return this.answers.concerns.join(',');
      if (step === 'name') return 'provided';
      return this.answers[step] || '';
    }

    updateNextState() {
      const stepEl = this.querySelector(`[data-hq-step="${this.current}"]`);
      if (!stepEl) return;
      const btn = stepEl.querySelector('[data-hq-next]');
      if (btn) btn.disabled = !this.isComplete(this.current);
    }

    updateProgress() {
      const isQuestion = QUESTION_STEPS.includes(this.current) || this.current === 'save';
      this.bar.hidden = !isQuestion;
      if (!isQuestion) return;
      const steps = this.flow().filter((s) => QUESTION_STEPS.includes(s));
      const index = this.current === 'save' ? steps.length : steps.indexOf(this.current) + 1;
      const pct = Math.round((Math.max(index - 1, 0) / steps.length) * 100);
      const shown = this.current === 'save' ? 100 : pct;
      this.ridge.style.strokeDashoffset = String(100 - shown);
      this.progress.setAttribute('aria-valuenow', String(shown));
      const text = (this.cfg.text.stepCount || '[current] of [total]')
        .replace('[current]', String(Math.min(index, steps.length)))
        .replace('[total]', String(steps.length));
      this.count.textContent = this.current === 'save' ? '' : text;
      this.progress.setAttribute('aria-valuetext', text);
    }

    fillName() {
      this.querySelectorAll('[data-hq-name]').forEach((el) => { el.textContent = this.answers.name; });
    }

    /* ----- Completion and saving ----- */
    onQuizComplete() {
      this.result = recommend(this.answers, this.isAvail);
      this.saveState = null;
      this.persist({ completedAt: new Date().toISOString(), routine: this.routineHandles() });
      track('hair_quiz_completed', {
        texture: this.answers.texture, coloured: this.answers.coloured, colour: this.answers.colour || '',
        scalp: this.answers.scalp, density: this.answers.density, concerns: this.answers.concerns.join(',')
      });
    }

    routineHandles() {
      if (!this.result) return [];
      const p = this.cfg.products || {};
      return this.result.routine.map((r) => p[r.key] && p[r.key].handle).filter(Boolean);
    }

    buildTags() {
      const a = this.answers;
      const tags = ['quiz-completed', `quiz-texture:${a.texture}`, `quiz-coloured:${a.coloured}`, `quiz-scalp:${a.scalp}`, `quiz-density:${a.density}`];
      if (a.coloured === 'yes' && a.colour) tags.push(`quiz-colour:${a.colour}`);
      a.concerns.forEach((c) => tags.push(`quiz-concern:${c.replace(/_/g, '-')}`));
      return tags.join(', ');
    }

    async submitSave(form) {
      const emailInput = form.querySelector('[data-hq-email]');
      const message = form.querySelector('[data-hq-save-message]');
      const submit = form.querySelector('[data-hq-save-submit]');
      const email = (emailInput.value || '').trim();

      message.hidden = true;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        emailInput.setAttribute('aria-invalid', 'true');
        message.textContent = this.cfg.text.emailError;
        message.hidden = false;
        emailInput.focus();
        return;
      }
      emailInput.removeAttribute('aria-invalid');

      form.querySelector('[data-hq-tags]').value = this.buildTags();
      form.querySelector('[data-hq-first-name]').value = this.answers.name || '';
      this.persist({});

      submit.disabled = true;
      submit.classList.add('is-loading');
      try {
        const res = await fetch(form.getAttribute('action') || '/contact', {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin'
        });
        // Shopify may ask for a captcha: continue with a normal submission, the quiz reopens on the results.
        if (/\/challenge/.test(res.url || '')) { form.submit(); return; }
        if (!res.ok) throw new Error('save');
        this.saveState = 'saved';
        this.persist({ savedToAccount: true });
        track('hair_quiz_saved', { type: this.cfg.loggedIn ? 'customer' : 'guest' });
      } catch (e) {
        this.saveState = 'error';
      } finally {
        submit.disabled = false;
        submit.classList.remove('is-loading');
      }
      this.go('results');
    }

    renderSaveNotice() {
      const el = this.querySelector('[data-hq-save-notice]');
      if (!el) return;
      if (this.saveState === 'saved') { el.textContent = this.cfg.text.savedNotice; el.hidden = false; }
      else if (this.saveState === 'error') { el.textContent = this.cfg.text.saveError; el.hidden = false; }
      else el.hidden = true;
    }

    showSavedResults() {
      const saved = store.read();
      if (!saved || !saved.answers) return;
      this.answers = Object.assign(emptyAnswers(), saved.answers);
      this.syncInputs();
      this.result = recommend(this.answers, this.isAvail);
      if (saved.savedToAccount) this.saveState = 'saved';
      this.go('results');
    }

    syncInputs() {
      const a = this.answers;
      const nameInput = this.querySelector('[data-hq-input="name"]');
      if (nameInput) nameInput.value = a.name || '';
      this.querySelectorAll('[data-hq-input]').forEach((input) => {
        const f = input.dataset.hqInput;
        if (input.type === 'radio') input.checked = a[f] === input.value;
        if (input.type === 'checkbox') input.checked = a.concerns.includes(input.value);
      });
    }

    retake() {
      const name = this.answers.name;
      this.answers = Object.assign(emptyAnswers(), { name });
      this.result = null;
      this.saveState = null;
      this.syncInputs();
      track('hair_quiz_retake');
      this.go('texture');
    }

    /* ----- Results ----- */
    renderResults() {
      if (!this.result) this.result = recommend(this.answers, this.isAvail);
      const { text, labels } = this.cfg;
      const p = this.cfg.products || {};
      const a = this.answers;
      const name = esc(a.name);

      const summary = [
        [text.summary.texture, labels.texture[a.texture]],
        [text.summary.colour, a.coloured === 'yes' ? (labels.colour[a.colour] || '') : text.summary.notColoured],
        [text.summary.scalp, labels.scalp[a.scalp]],
        [text.summary.density, labels.density[a.density]],
        [text.summary.concerns, a.concerns.map((c) => labels.concerns[c]).join(', ')]
      ].filter((row) => row[1]);

      const routine = this.result.routine.filter((r) => p[r.key]);

      const productButton = (prod) => prod.available
        ? `<button type="button" class="hq-btn hq-btn--small" data-hq-add="${esc(prod.variantId)}">${esc(text.addLabel)}</button>`
        : `<span class="hq-soldout">${esc(text.soldOutLabel)}</span>`;

      const media = (prod, cls) => prod.image
        ? `<a class="${cls}" href="${esc(prod.url)}" tabindex="-1" aria-hidden="true"><img src="${esc(prod.image)}" alt="" loading="lazy" width="240" height="240"></a>`
        : `<span class="${cls} ${cls}--empty" aria-hidden="true"></span>`;

      const routineHtml = routine.map((r, i) => {
        const prod = p[r.key];
        return `
          <li class="hq-routine__item">
            <span class="hq-routine__num" aria-hidden="true">${i + 1}</span>
            ${media(prod, 'hq-routine__media')}
            <div class="hq-routine__body">
              <p class="hq-routine__step">${esc(text.steps[r.step] || '')}</p>
              <h3 class="hq-routine__title"><a href="${esc(prod.url)}">${esc(prod.title)}</a></h3>
              <p class="hq-routine__reason">${esc(r.reason)}</p>
              <div class="hq-routine__buy">
                <span class="hq-price">${esc(prod.price)}</span>
                ${productButton(prod)}
              </div>
            </div>
          </li>`;
      }).join('');

      const setInfo = this.result.set;
      const setProd = setInfo && p[setInfo.key] && p[setInfo.key].available ? p[setInfo.key] : null;
      let setHtml = '';
      if (setProd) {
        const stepNums = setInfo.covered
          .map((k) => routine.findIndex((r) => r.key === k) + 1)
          .filter((n) => n > 0)
          .sort((x, y) => x - y);
        const joinList = (list) => (list.length > 1 ? `${list.slice(0, -1).join(', ')} ${text.andWord || 'and'} ${list[list.length - 1]}` : list.join(''));
        const coversLine = (text.setCovers || 'Steps [steps] of this routine in one value set.').replace('[steps]', joinList(stepNums.map(String)));
        const extraTitles = setInfo.extras.map((k) => p[k] && p[k].title).filter(Boolean);
        const extraLine = extraTitles.length ? ` ${(text.setExtra || 'Also includes [products].').replace('[products]', joinList(extraTitles))}` : '';
        setHtml = `
        <aside class="hq-set">
          ${media(setProd, 'hq-set__media')}
          <div class="hq-set__body">
            <h3 class="hq-set__heading">${esc(text.setHeading)}</h3>
            <p class="hq-set__text">${esc(coversLine + extraLine)}</p>
            <p class="hq-set__title"><a href="${esc(setProd.url)}">${esc(setProd.title)}</a> <span class="hq-price">${esc(setProd.price)}</span></p>
            <div class="hq-set__buy">${productButton(setProd)}</div>
          </div>
        </aside>`;
      }

      const swaps = this.result.swaps.filter((s) => p[s.key]);
      const swapHtml = swaps.length ? `
        <div class="hq-swaps">
          <h3 class="hq-swaps__heading">${esc(text.swapHeading)}</h3>
          <p class="hq-swaps__text">${esc(text.swapText)}</p>
          <ul class="hq-swaps__list">
            ${swaps.map((s) => {
              const prod = p[s.key];
              return `<li class="hq-swap">
                ${media(prod, 'hq-swap__media')}
                <div class="hq-swap__body">
                  <a class="hq-swap__title" href="${esc(prod.url)}">${esc(prod.title)}</a>
                  <span class="hq-swap__reason">${esc(s.reason)}</span>
                </div>
                <span class="hq-price">${esc(prod.price)}</span>
              </li>`;
            }).join('')}
          </ul>
        </div>` : '';

      const anyAvailable = routine.some((r) => p[r.key].available);

      this.resultsRoot.innerHTML = `
        <header class="hq-results__head">
          <h2 class="hq-title hq-title--xl" tabindex="-1">${esc(text.resultsHeading).replace('[name]', name)}</h2>
          ${text.resultsText ? `<p class="hq-lede">${esc(text.resultsText)}</p>` : ''}
          <p class="hq-notice" data-hq-save-notice role="status" hidden></p>
        </header>
        <section class="hq-profile" aria-label="${esc(text.profileLabel)}">
          <h3 class="hq-profile__heading">${esc(text.profileLabel)}</h3>
          <dl class="hq-profile__list">
            ${summary.map(([k, v]) => `<div class="hq-profile__row"><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')}
          </dl>
        </section>
        <ol class="hq-routine" data-hq-routine>${routineHtml}</ol>
        <div class="hq-results__cta">
          ${anyAvailable ? `<button type="button" class="hq-btn hq-btn--primary" data-hq-add-all>${esc(text.addAllLabel)}</button>` : ''}
          <p class="hq-message" data-hq-cart-message role="status" hidden></p>
        </div>
        ${setHtml}
        ${swapHtml}
        <p class="hq-retake"><a href="#" data-hq-retake>${esc(text.retakeLabel)}</a></p>
      `;
      this.renderSaveNotice();
      track('hair_quiz_results_viewed', { products: this.routineHandles().join(','), set: (this.result.set && this.result.set.key) || '' });
    }

    /* ----- Cart ----- */
    // Refreshes and opens the Align cart drawer (window.updateMiniCart from section-main-cart.js).
    // Falls back to the cart page when the drawer is not available.
    async openCartDrawer() {
      const drawer = () => document.querySelector('drawer-component.mini-cart');
      try {
        if (typeof window.updateMiniCart === 'function') {
          await window.updateMiniCart(true);
        } else if (window.cartService && typeof window.cartService.updateMiniCart === 'function') {
          await window.cartService.updateMiniCart(true);
        } else {
          window.location.href = this.cfg.cartUrl;
          return;
        }
        const d = drawer();
        if (!d) { window.location.href = this.cfg.cartUrl; return; }
        if (!d.classList.contains('open') && typeof d.open === 'function') d.open();
      } catch (e) {
        window.location.href = this.cfg.cartUrl;
      }
    }

    async addToCart(variantIds, button) {
      const ids = variantIds.filter(Boolean);
      if (!ids.length) return;
      const message = this.querySelector('[data-hq-cart-message]');
      const { text } = this.cfg;
      button.disabled = true;
      button.classList.add('is-loading');
      try {
        const res = await fetch(this.cfg.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) })
        });
        if (!res.ok) throw new Error('cart');
        await res.json();
        track('hair_quiz_add_to_cart', { variants: ids.join(','), count: ids.length });

        if (this.cfg.afterAdd === 'redirect') { window.location.href = this.cfg.cartUrl; return; }
        const markAdded = (b) => { b.textContent = text.addedLabel; b.classList.add('is-added'); b.disabled = true; };
        if (button.hasAttribute('data-hq-add-all')) {
          this.querySelectorAll('[data-hq-routine] [data-hq-add]').forEach(markAdded);
          button.disabled = false;
        } else {
          markAdded(button);
        }
        if (message) message.hidden = true;
        await this.openCartDrawer();
      } catch (e) {
        button.disabled = false;
        if (message) { message.textContent = text.addError; message.hidden = false; }
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('hair-quiz', HairQuiz);
})();
